import React from 'react';
import {
  Modal,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { dateFormat } from '../utils/dateFormat';

interface TaskRow {
  date: string;
  comments: string;
  hours: string;
}

interface ViewActivityTasksModalProps {
  open: boolean;
  onClose: () => void;
  tasks: TaskRow[];
  activityName?: string;
}

const ViewActivityTasksModal: React.FC<ViewActivityTasksModalProps> = ({ open, onClose, tasks, activityName }) => {
  // Sort tasks by date ascending (earliest to latest)
  const sortedTasks = [...tasks].sort((a, b) => {
    // Try to parse as ISO or DD-MMM-YYYY
    const parse = (d: string) => {
      // Try ISO first
      const iso = Date.parse(d);
      if (!isNaN(iso)) return iso;
      // Try DD-MMM-YYYY
      const parts = d.match(/(\d{2})-([A-Za-z]{3})-(\d{4})/);
      if (parts) {
        const [_, day, mon, year] = parts;
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        const monthIdx = months.findIndex(m => m.toLowerCase() === mon.toLowerCase());
        if (monthIdx !== -1) {
          return Date.UTC(Number(year), monthIdx, Number(day));
        }
      }
      return 0;
    };
    return parse(a.date) - parse(b.date);
  });

  return (
    <Modal
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }}
      disableEscapeKeyDown
    >
      {/* Detect dark mode */}
      {(() => {
        const isDark = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
        return (
          <Box
            sx={{
              width: 540,
              bgcolor: isDark ? '#1e293b' : '#fff',
              borderRadius: 3,
              boxShadow: 24,
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              maxHeight: '70vh',
              display: 'flex',
              flexDirection: 'column',
              p: 0,
              border: isDark ? '1px solid #334155' : undefined,
            }}
          >
            <Box
              px={3}
              pt={2.5}
              pb={1.5}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              borderBottom={isDark ? '1px solid #334155' : '1px solid #e0e0e0'}
              sx={{ background: isDark ? '#1e293b' : '#fff' }}
            >
              <Typography fontSize={18} fontWeight={700} sx={{ color: isDark ? '#fff' : '#0B3A6A', lineHeight: 1.2 }}>
                Task Details{activityName ? ` - ${activityName}` : ''}
              </Typography>
              <IconButton onClick={onClose} size="small" sx={{ ml: 1, color: isDark ? '#38bdf8' : undefined }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 'none',
                mx: 0,
                my: 0,
                maxHeight: 340,
                borderRadius: 0,
                background: isDark ? '#1e293b' : '#fff',
              }}
            >
              <Table size="small" sx={{ minWidth: 500, background: isDark ? '#1e293b' : '#fff' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: 110, background: isDark ? '#334155' : '#f7fafd', color: isDark ? '#fff' : undefined, borderBottom: isDark ? '1px solid #334155' : undefined }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 320, background: isDark ? '#334155' : '#f7fafd', color: isDark ? '#fff' : undefined, borderBottom: isDark ? '1px solid #334155' : undefined }}>Comments</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 60, background: isDark ? '#334155' : '#f7fafd', color: isDark ? '#fff' : undefined, textAlign: 'center', borderBottom: isDark ? '1px solid #334155' : undefined }}>Hours</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedTasks.map((row, idx) => (
                    <TableRow key={idx} sx={{ background: isDark ? '#1e293b' : undefined }}>
                      <TableCell sx={{ verticalAlign: 'top', fontSize: 14, color: isDark ? '#f1f5f9' : undefined }}>{dateFormat(row.date)}</TableCell>
                      <TableCell sx={{ verticalAlign: 'top', fontSize: 14, whiteSpace: 'pre-line', wordBreak: 'break-word', maxWidth: 320, minWidth: 120, color: isDark ? '#f1f5f9' : undefined }}>{row.comments}</TableCell>
                      <TableCell sx={{ verticalAlign: 'top', fontSize: 14, textAlign: 'center', color: isDark ? '#f1f5f9' : undefined }}>{row.hours}</TableCell>
                    </TableRow>
                  ))}
                  {sortedTasks.length === 0 && (
                    <TableRow sx={{ background: isDark ? '#1e293b' : undefined }}>
                      <TableCell colSpan={3} align="center" sx={{ color: isDark ? '#aaa' : '#aaa' }}>
                        No tasks found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      })()}
    </Modal>
  );
};

export default ViewActivityTasksModal;
